<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15">
      <Form ref="sessionForm" @submit="updateSession" :validation-schema="sessionSchema">
        <div class="row">
          <div class="col-md-4">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Nombre de questions <span class="text-danger">*</span>
              </label>
              <Field
                name="count"
                type="number"
                class="form-control shadow-none text-black"
                placeholder="Entrer le nombre de questions"
                v-model="formData.count"
              />
              <ErrorMessage name="count" class="text-danger" />
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Niveau <span class="text-danger">*</span>
              </label>
              <Field name="niveau" v-slot="{ field }">
                <Multiselect
                  :options="niveauOptions"
                  :searchable="true"
                  track-by="label"
                  label="label"
                  v-model="formData.niveau"
                  v-bind="field"
                  placeholder="Sélectionner un niveau"
                />
              </Field>
              <ErrorMessage name="niveau" class="text-danger" />
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Matière <span class="text-danger">*</span>
              </label>
              <Field name="matiere" v-slot="{ field }">
                <Multiselect
                  :options="matiereOptions"
                  :searchable="true"
                  track-by="label"
                  label="label"
                  v-model="formData.matiere"
                  v-bind="field"
                  placeholder="Sélectionner une matière"
                />
              </Field>
              <ErrorMessage name="matiere" class="text-danger" />
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Filière
              </label>
              <Field name="filiere" v-slot="{ field }">
                <Multiselect
                  :options="filiereOptions"
                  :searchable="true"
                  track-by="label"
                  label="label"
                  v-model="formData.filiere"
                  v-bind="field"
                  placeholder="Sélectionner une filière (facultatif)"
                />
              </Field>
              <ErrorMessage name="filiere" class="text-danger" />
            </div>
          </div>
        </div>
        <div class="col-md-12 mt-5">
          <div class="d-flex align-items-center">
            <button
              class="btn btn-success me-3"
              type="submit"
              :disabled="isSubmitting"
              :class="{ 'cursor-not-allowed': isSubmitting }"
            >
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
              Modifier la session
            </button>
            <router-link
              to="/liste-sessions"
              class="btn btn-danger"
            >
              <i class="fa fa-times me-1"></i>
              Annuler
            </router-link>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, onMounted } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import { useRouter, useRoute } from 'vue-router';
import ApiService from '@/services/ApiService';
import { success, error } from '@/utils/utils';
import Multiselect from '@vueform/multiselect';

export default defineComponent({
  name: 'EditSessionPage',
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const sessionForm = ref<InstanceType<typeof Form> | null>(null);
    const isSubmitting = ref(false);

    const formData = reactive({
      count: 0,
      niveau: '',
      matiere: '',
      filiere: '',
    });

    const niveauOptions = ref([]);
    const matiereOptions = ref([]);
    const filiereOptions = ref([]);

    const sessionSchema = Yup.object().shape({
      count: Yup.number()
        .min(1, 'Le nombre de questions doit être supérieur à 0')
        .required('Le nombre de questions est obligatoire'),
      niveau: Yup.string().required('Le niveau est obligatoire'),
      matiere: Yup.string().required('La matière est obligatoire'),
      filiere: Yup.string().nullable().notRequired(),
    });

    const loadNiveaux = async () => {
      try {
        const { data } = await ApiService.get('/all/niveaux');
        niveauOptions.value = data.data.data.map((niveau: any) => ({
          value: niveau.id,
          label: niveau.libelle,
        }));
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement des niveaux');
      }
    };

    const loadMatieres = async () => {
      try {
        const { data } = await ApiService.get('/all/matieres');
        matiereOptions.value = data.data.data.map((matiere: any) => ({
          value: matiere.id,
          label: matiere.libelle,
        }));
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement des matières');
      }
    };

    const loadFilieres = async () => {
      try {
        const { data } = await ApiService.get('/all/filieres');
        filiereOptions.value = data.data.data.map((filiere: any) => ({
          value: filiere.id,
          label: filiere.libelle,
        }));
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement des filières');
      }
    };

    const loadSession = async () => {
      try {
        const sessionId = route.params.id;
        const { data } = await ApiService.get(`/sessions/${sessionId}`);
        if (data.code === 200 && data.data) {
          formData.count = data.data.count;
          formData.niveau = data.data.filiereNiveauMatiere?.niveau?.id || '';
          formData.matiere = data.data.filiereNiveauMatiere?.matiere?.id || '';
          formData.filiere = data.data.filiereNiveauMatiere?.filiere?.id || '';
        } else {
          throw new Error('Session non trouvée');
        }
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement de la session');
      }
    };

    const updateSession = async (values: any) => {
      try {
        isSubmitting.value = true;
        const sessionId = route.params.id;
        const niveauValue = typeof formData.niveau === 'object' ? (formData.niveau as any).value : formData.niveau;
        const matiereValue = typeof formData.matiere === 'object' ? (formData.matiere as any).value : formData.matiere;
        const filiereValue = typeof formData.filiere === 'object' ? (formData.filiere as any).value : formData.filiere;

        const sessionData = {
          count: formData.count,
          niveauId: niveauValue,
          matiereId: matiereValue,
          filiereId: filiereValue || null,
        };

        const { data } = await ApiService.put(`/sessions/${sessionId}`, sessionData);
        if (data.code === 200) {
          success(data.message);
          router.push({ name: 'ListeSession' });
        }
      } catch (err: any) {
    //    error(err.response?.data?.message || 'Erreur lors de la mise à jour de la session');
      } finally {
        isSubmitting.value = false;
      }
    };

    onMounted(() => {
      loadNiveaux();
      loadMatieres();
      loadFilieres();
      loadSession();
    });

    return {
      sessionForm,
      formData,
      sessionSchema,
      niveauOptions,
      matiereOptions,
      filiereOptions,
      updateSession,
      isSubmitting,
    };
  },
});
</script>

<style scoped>
.card {
  border-radius: 0;
}

.form-control,
.form-check-input {
  border-radius: 0;
}

.text-black {
  color: #000;
}

.text-danger {
  font-size: 0.875rem;
}

.border-primary {
  border-width: 2px;
}

.btn-success,
.btn-danger {
  border-radius: 0;
  transition: all 0.2s ease;
}

.btn:hover {
  filter: brightness(90%);
}

.cursor-not-allowed {
  cursor: not-allowed;
  opacity: 0.7;
}

.mb-10 {
  margin-bottom: 10px;
}

.mt-5 {
  margin-top: 5px;
}
</style>